import React, { useState } from 'react';
import { Sparkle, X, Play } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * DemoBadge — subtle pill that only renders when `user.is_demo === true`.
 * Click opens a founder-only modal with the suggested walkthrough
 * (pulled from `user.demo_guide` if present, else a safe default).
 *
 * Visible only on the Home header; deliberately unobtrusive.
 */
export const DemoBadge = ({ user }) => {
  const [open, setOpen] = useState(false);
  if (!user?.is_demo) return null;

  const guide = user.demo_guide || {};

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="demo-badge"
        title="Investor Demo Mode — click for founder walkthrough"
        className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300/60 bg-emerald-50/80 px-2.5 py-1 min-h-[44px] text-[11px] font-medium text-emerald-900 hover:bg-emerald-100 transition-colors"
      >
        <Sparkle size={12} weight="fill" className="text-emerald-600" />
        Investor Demo Mode
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setOpen(false)}
            data-testid="demo-badge-modal-backdrop"
          >
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 10, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 220, damping: 22 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden"
              data-testid="demo-badge-modal"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <Sparkle size={16} weight="fill" className="text-emerald-600" />
                  <h3 className="text-sm font-semibold text-slate-900">
                    Founder walkthrough — synthetic demo account
                  </h3>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  data-testid="demo-badge-modal-close"
                  className="text-slate-400 hover:text-slate-700 transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
                {guide.summary && (
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {guide.summary}
                  </p>
                )}

                {Array.isArray(guide.walkthrough) && guide.walkthrough.length > 0 && (
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
                      <Play size={12} weight="fill" />
                      Recommended 5-minute walkthrough
                    </div>
                    <ol className="space-y-2">
                      {guide.walkthrough.map((step, i) => (
                        <li key={i} className="flex gap-3 text-sm text-slate-700">
                          <span className="shrink-0 w-5 h-5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-semibold flex items-center justify-center">
                            {i + 1}
                          </span>
                          <span className="leading-relaxed">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {guide.picks && (
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-2">
                      Best things to open
                    </div>
                    <div className="space-y-2 text-sm text-slate-700">
                      {Object.entries(guide.picks).map(([k, v]) => (
                        <div key={k} className="flex gap-3">
                          <span className="text-slate-400 shrink-0 w-40">{k.replace(/_/g, ' ')}</span>
                          <span className="font-medium text-slate-800">{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {guide.live_question && (
                  <div className="rounded-lg bg-emerald-50/60 border border-emerald-100 px-4 py-3">
                    <div className="text-[11px] uppercase tracking-wider text-emerald-800 mb-1">
                      Best live question to ask
                    </div>
                    <p className="text-sm text-emerald-900 italic">"{guide.live_question}"</p>
                  </div>
                )}

                {guide.warnings && Array.isArray(guide.warnings) && guide.warnings.length > 0 && (
                  <div className="text-xs text-slate-500 border-t border-slate-100 pt-3">
                    <span className="font-semibold text-slate-600">Watch for:</span>{' '}
                    {guide.warnings.join(' · ')}
                  </div>
                )}

                <div className="text-[11px] text-slate-400 pt-2 border-t border-slate-100">
                  This account is flagged <code className="text-slate-600">is_demo: true</code> in the database. Not a real patient.
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default DemoBadge;
